import React, { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";

/**
 * Fullscreen image lightbox with prev/next, keyboard nav (←/→/Esc), and
 * click-outside-to-close. Renders `null` when not open — no perf cost.
 */
export default function Lightbox({ images = [], startIndex = 0, open, onClose, alt = "Property image" }) {
    const [idx, setIdx] = useState(startIndex);

    useEffect(() => { setIdx(startIndex); }, [startIndex, open]);

    const prev = useCallback(() => setIdx((i) => (i - 1 + images.length) % images.length), [images.length]);
    const next = useCallback(() => setIdx((i) => (i + 1) % images.length), [images.length]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            else if (e.key === "ArrowLeft") prev();
            else if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", onKey);
        // lock body scroll
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, prev, next, onClose]);

    if (!open || !images.length) return null;

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
            data-testid="lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={alt}
            onClick={onClose}
        >
            {/* Top bar */}
            <div className="flex items-center justify-between px-4 md:px-8 py-4 md:py-6 text-white/80">
                <div className="text-[10px] md:text-[11px] uppercase tracking-[0.24em] font-bold">
                    <span className="text-[var(--ebg-gold)]">Image</span> · {idx + 1} / {images.length}
                </div>
                <button
                    data-testid="lightbox-close"
                    onClick={(e) => { e.stopPropagation(); onClose(); }}
                    className="w-11 h-11 rounded-full bg-white/5 hover:bg-[var(--ebg-gold)] hover:text-black border border-white/15 flex items-center justify-center transition-colors"
                    aria-label="Close"
                >
                    <X size={20}/>
                </button>
            </div>

            {/* Image */}
            <div className="flex-1 relative flex items-center justify-center px-4 md:px-16 min-h-0" onClick={(e) => e.stopPropagation()}>
                <img
                    src={images[idx]}
                    alt={`${alt} — ${idx + 1}`}
                    className="w-auto h-auto max-w-full max-h-full object-contain shadow-[0_25px_100px_-20px_rgba(0,0,0,0.5)]"
                    style={{ maxHeight: "100%", maxWidth: "100%" }}
                    data-testid="lightbox-image"
                />

                {images.length > 1 && (
                    <>
                        <button
                            data-testid="lightbox-prev"
                            onClick={(e) => { e.stopPropagation(); prev(); }}
                            aria-label="Previous"
                            className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/5 hover:bg-[var(--ebg-gold)] hover:text-black border border-white/15 flex items-center justify-center text-white transition-colors"
                        >
                            <ChevronLeft size={22}/>
                        </button>
                        <button
                            data-testid="lightbox-next"
                            onClick={(e) => { e.stopPropagation(); next(); }}
                            aria-label="Next"
                            className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/5 hover:bg-[var(--ebg-gold)] hover:text-black border border-white/15 flex items-center justify-center text-white transition-colors"
                        >
                            <ChevronRight size={22}/>
                        </button>
                    </>
                )}
            </div>

            {/* Thumbstrip */}
            {images.length > 1 && (
                <div
                    className="px-4 md:px-8 py-4 md:py-5 flex gap-2 overflow-x-auto no-scrollbar"
                    onClick={(e) => e.stopPropagation()}
                >
                    {images.map((im, i) => (
                        <button
                            key={i}
                            data-testid={`lightbox-thumb-${i}`}
                            onClick={() => setIdx(i)}
                            className={`shrink-0 w-16 h-12 md:w-20 md:h-14 overflow-hidden border transition-all ${i === idx ? "border-[var(--ebg-gold)] opacity-100" : "border-white/15 opacity-50 hover:opacity-90"}`}
                        >
                            <img src={im} alt="" className="w-full h-full object-cover"/>
                        </button>
                    ))}
                </div>
            )}

            {/* Bottom hint */}
            <div className="hidden md:block px-8 pb-4 text-[10px] uppercase tracking-[0.24em] text-white/40">
                <span className="text-[var(--ebg-gold)]">Esc</span> to close &nbsp;·&nbsp; <span className="text-[var(--ebg-gold)]">←</span> / <span className="text-[var(--ebg-gold)]">→</span> to navigate
            </div>
        </div>
    );
}

/** Small icon overlay to hint a clickable image (use inside a relative parent). */
export function ZoomHint({ className = "" }) {
    return (
        <span className={`absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 backdrop-blur border border-white/15 text-white flex items-center justify-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity ${className}`}>
            <ZoomIn size={14}/>
        </span>
    );
}
